import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  Eye,
  Search,
  RefreshCw,

  Trash2,
  Check,
  CheckCircle,
  AlertTriangle,
  EyeOff
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useLeads } from '../../hooks/useSupabase';
import { useAuth, mockUsers } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import LeadDetailModal from '../Modals/LeadDetailModal';
import EditLeadModal from '../Modals/EditLeadModal';

interface FollowUpItem {
  id: string;
  type: 'main' | 'extra';
  date: string | null;
  isCompleted: boolean;
  completedAt: string | null;
  lead: any;
  title?: string;
  priority: string;
  created_by?: string;
  isWatched: boolean;
}

const FollowUps: React.FC = () => {
  const { leads, loading, fetchLeads, updateLead } = useLeads();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [createdByFilter, setCreatedByFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date-asc' | 'date-desc' | 'alpha-asc' | 'alpha-desc'>('date-asc');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [extraReminders, setExtraReminders] = useState<any[]>([]); // New state for reminders

  // Fetch leads, reminders, and profiles when component mounts
  useEffect(() => {
    fetchLeads();
    fetchExtraReminders();
    fetchProfiles();
  }, [user?.id]);

  const fetchExtraReminders = async () => {
    try {
      const { data, error } = await supabase
        .from('reminders')
        .select('*, lead:leads(*)')
        .order('date', { ascending: true });

      if (error) {
        // If table doesn't exist yet (404), just ignore to prevent page crash
        if (error.code === '42P01') return;
        console.error('Reminders fetch error:', error);
        return;
      }
      setExtraReminders(data || []);
    } catch (err) {
      console.error('Reminders fetch catch:', err);
    }
  };

  const fetchProfiles = async () => {
    if (!user?.id) return;

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('branch_id')
        .eq('id', user.id)
        .maybeSingle();

      const branchId = profile?.branch_id;

      if (branchId) {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, name, email, role, unit_type, branch_id, is_active')
          .eq('branch_id', branchId)
          .order('name');

        if (error) {
          console.error('Profil yükleme hatası:', error);
          return;
        }

        setProfiles(data || []);
      }
    } catch (err) {
      console.error('Profil fetch hatası:', err);
    }
  };

  const getCreatorName = (createdById: string) => {
    const profile = profiles.find(p => p.id === createdById);
    return profile?.name || 'Bilinmiyor';
  };

  const getCreatorColor = (name: string) => {
    if (name === 'Sistemden Yüklendi' || name === 'Bilinmiyor') return { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-800' };
    if (name.includes('Sezin')) return { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-700 dark:text-pink-300', border: 'border-pink-200 dark:border-pink-800' };
    if (name.includes('Medine')) return { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-200 dark:border-green-800' };
    if (name.includes('Edanur')) return { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800' };
    if (name.includes('Betül')) return { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-cyan-300', border: 'border-cyan-200 dark:border-cyan-800' };
    if (name.includes('Kaya')) return { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-800' };
    return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-700' };
  };

  // Normalize and Combine
  const getUnifiedItems = () => {
    const mainItems = (leads || []).map(lead => ({
      id: lead.id,
      type: 'main' as const,
      date: lead.follow_up_date,
      isCompleted: lead.follow_up_completed,
      completedAt: lead.follow_up_completed_at,
      lead: lead,
      title: 'Genel Takip',
      priority: lead.priority,
      created_by: lead.created_by,
      isWatched: lead.follow_up_is_watched || false
    })).filter(item => item.date); // Only those with date

    const extraItems = extraReminders.map(rem => ({
      id: rem.id,
      type: 'extra' as const,
      date: rem.date,
      isCompleted: rem.is_completed,
      completedAt: null, // Reminder table handling might need completed_at if filtering by it
      lead: rem.lead,
      title: rem.title,
      priority: rem.lead?.priority || 'medium',
      created_by: rem.created_by,
      isWatched: rem.is_watched || false
    }));

    return [...mainItems, ...extraItems];
  };

  const allItems = getUnifiedItems();

  // Filter items
  const filteredItems = allItems.filter(item => {
    // Tab Filter
    if (activeTab === 'active') {
      if (item.type === 'main' && (item.lead.follow_up_required !== true || item.isCompleted)) return false;
      if (item.type === 'extra' && item.isCompleted) return false;
    } else {
      if (!item.isCompleted) return false;
    }

    // Search
    const searchString = searchTerm.toLowerCase();
    const l = item.lead || {};
    const matchesSearch =
      l.first_name?.toLowerCase().includes(searchString) ||
      l.last_name?.toLowerCase().includes(searchString) ||
      l.email?.toLowerCase().includes(searchString) ||
      l.phone?.includes(searchTerm) ||
      item.title?.toLowerCase().includes(searchString);

    if (!matchesSearch) return false;

    // Filters
    if (priorityFilter !== 'all' && item.priority !== priorityFilter) return false;
    if (createdByFilter !== 'all' && item.created_by !== createdByFilter) return false;

    // Date Filter
    if (dateFilter !== 'all' && item.date) {
      const itemDate = new Date(item.date);
      const today = new Date();
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

      switch (dateFilter) {
        case 'today':
          return itemDate.toDateString() === today.toDateString();
        case 'tomorrow':
          return itemDate.toDateString() === tomorrow.toDateString();
        case 'this-week':
          return itemDate <= nextWeek;
        case 'overdue':
          return itemDate < today;
      }
    }

    return true;
  });

  // Sort logic
  filteredItems.sort((a, b) => {
    switch (sortBy) {
      case 'date-asc':
        return new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime();
      case 'date-desc':
        return new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime();
      case 'alpha-asc': {
        const nameA = `${a.lead?.first_name || ''} ${a.lead?.last_name || ''}`.toLowerCase();
        const nameB = `${b.lead?.first_name || ''} ${b.lead?.last_name || ''}`.toLowerCase();
        return nameA.localeCompare(nameB, 'tr');
      }
      case 'alpha-desc': {
        const nameA = `${a.lead?.first_name || ''} ${a.lead?.last_name || ''}`.toLowerCase();
        const nameB = `${b.lead?.first_name || ''} ${b.lead?.last_name || ''}`.toLowerCase();
        return nameB.localeCompare(nameA, 'tr');
      }
      default:
        return 0;
    }
  });


  const getFollowUpStatus = (lead: any) => {
    if (!lead.follow_up_date) return 'no-date';

    const followUpDate = new Date(lead.follow_up_date);
    const today = new Date();

    if (followUpDate < today) return 'overdue';
    if (followUpDate.toDateString() === today.toDateString()) return 'today';
    if (followUpDate.getTime() - today.getTime() <= 24 * 60 * 60 * 1000) return 'tomorrow';
    return 'upcoming';
  };

  const getFollowUpStatusColor = (status: string) => {
    switch (status) {
      case 'overdue': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'today': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'tomorrow': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'upcoming': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getFollowUpStatusText = (status: string) => {
    switch (status) {
      case 'overdue': return 'Gecikmiş';
      case 'today': return 'Bugün';
      case 'tomorrow': return 'Yarın';
      case 'upcoming': return 'Yaklaşan';
      default: return 'Tarih Yok';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const handleLeadAction = async (action: string, item: any) => {
    const lead = item.lead;
    if (!lead) return;

    setSelectedLead(lead);

    switch (action) {
      case 'view':
        setShowDetailModal(true);
        break;
      case 'edit':
        setShowEditModal(true);
        break;
      case 'call':
        if (window.makeKarelCall && lead.phone) {
          const success = window.makeKarelCall(lead.phone);
          if (success) {
            console.log(`${lead.first_name} ${lead.last_name} aranıyor: ${lead.phone}`);
          } else {
            const cleanPhone = lead.phone.replace(/\D/g, '');
            const message = encodeURIComponent(`Merhaba ${lead.first_name}, Pisa Educazione'den arıyorum. Size nasıl yardımcı olabilirim?`);
            window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
          }
        } else {
          alert('Telefon numarası bulunamadı.');
        }
        break;
      case 'email':
        if (lead.email) {
          const subject = encodeURIComponent('Pisa Educazione - Follow Up Görüşmesi');
          const body = encodeURIComponent(`Merhaba ${lead.first_name},\n\nPisa Educazione'den size ulaşıyorum. Daha önce konuştuğumuz eğitim fırsatları hakkında takip görüşmesi yapmak isterim.\n\nSaygılarımla,\n${user?.name || 'Danışman'}`);
          const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${lead.email}&su=${subject}&body=${body}`;
          window.open(gmailUrl, '_blank');
        }
        break;
      case 'complete':
        try {
          if (item.type === 'main') {
            await updateLead(lead.id, {
              follow_up_completed: true,
              follow_up_completed_at: new Date().toISOString(),
              last_contact: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          } else {
            const { error } = await supabase.from('reminders').update({ is_completed: true }).eq('id', item.id);
            if (error) throw error;
            fetchExtraReminders();
          }
          await fetchLeads();
          alert('Follow up tamamlandı olarak işaretlendi!');
        } catch (error) {
          console.error('Follow up güncelleme hatası:', error);
          alert('Follow up güncellenirken hata oluştu.');
        }
        break;
      case 'delete':
        if (await window.appConfirm(`${item.type === 'main' ? 'Lead\'i' : 'Takibi'} silmek istediğinize emin misiniz?`)) {
          try {
            if (item.type === 'main') {
              const { error } = await supabase.from('leads').delete().eq('id', lead.id);
              if (error) throw error;
              fetchLeads();
            } else {
              const { error } = await supabase.from('reminders').delete().eq('id', item.id);
              if (error) throw error;
              fetchExtraReminders();
            }
            alert('Silme işlemi başarılı!');
          } catch (error) {
            console.error('Silme hatası:', error);
            alert('Silinirken hata oluştu.');
          }
        }
        break;
    }
  };

  const handleToggleWatched = async (item: FollowUpItem) => {
    try {
      if (item.type === 'main') {
        const { error } = await supabase
          .from('leads')
          .update({ follow_up_is_watched: !item.isWatched })
          .eq('id', item.id);
        if (error) throw error;
        await fetchLeads(); // Refresh to show changes
      } else {
        const { error } = await supabase
          .from('reminders')
          .update({ is_watched: !item.isWatched })
          .eq('id', item.id);
        if (error) throw error;
        fetchExtraReminders();
      }
    } catch (err) {
      console.error('Toggle watched error:', err);
      alert('İzleme durumu değiştirilirken hata oluştu.');
    }
  };



  // Stats hesapla
  // Stats hesapla - Updated for Unified Items
  const stats = {
    totalFollowUps: allItems.filter(i => (i.type === 'main' ? !i.lead.follow_up_completed : !i.isCompleted)).length,
    todayFollowUps: allItems.filter(i => (i.type === 'main' ? !i.lead.follow_up_completed : !i.isCompleted) && getFollowUpStatus({ follow_up_date: i.date }) === 'today').length,
    overdueFollowUps: allItems.filter(i => (i.type === 'main' ? !i.lead.follow_up_completed : !i.isCompleted) && getFollowUpStatus({ follow_up_date: i.date }) === 'overdue').length,
    completedFollowUps: allItems.filter(i => (i.type === 'main' ? i.lead.follow_up_completed : i.isCompleted)).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Follow Up'lar</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Takip görüşmesi gerektiren lead'ler • Toplam {filteredItems.length} follow up
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { fetchLeads(); fetchExtraReminders(); }}
            disabled={loading}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Yenile</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-gray-200 dark:border-gray-800">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-6 py-3 font-medium transition-colors ${activeTab === 'active'
            ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
        >
          Aktif Follow Up'lar ({stats.totalFollowUps})
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`px-6 py-3 font-medium transition-colors ${activeTab === 'completed'
            ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
        >
          Tamamlananlar ({stats.completedFollowUps})
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Calendar className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalFollowUps}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Aktif Follow Up</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Clock className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.todayFollowUps}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Bugün</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.overdueFollowUps}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Gecikmiş</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.completedFollowUps}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tamamlanan</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Follow up ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Öncelikler</option>
            <option value="high">Yüksek</option>
            <option value="medium">Orta</option>
            <option value="low">Düşük</option>
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Tarihler</option>
            <option value="overdue">Gecikmiş</option>
            <option value="today">Bugün</option>
            <option value="tomorrow">Yarın</option>
            <option value="this-week">Bu Hafta</option>
          </select>

          <select
            value={createdByFilter}
            onChange={(e) => setCreatedByFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Danışmanlar</option>
            {profiles
              .filter((profile) => profile.unit_type === 'sales' && profile.is_active !== false)
              .map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
              ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="date-asc">Tarihe Göre (Önce Eski)</option>
            <option value="date-desc">Tarihe Göre (Önce Yeni)</option>
            <option value="alpha-asc">İsim (A-Z)</option>
            <option value="alpha-desc">İsim (Z-A)</option>
          </select>
        </div>
      </div>

      {/* Follow Ups List */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Follow up'lar yükleniyor...</span>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {searchTerm ? 'Follow up bulunamadı' : 'Henüz follow up yok'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm
                ? 'Arama kriterlerinizi değiştirmeyi deneyin'
                : 'Lead eklerken follow up seçeneğini işaretleyin'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-6">
            {filteredItems.map((item) => {
              const lead = item.lead || {};
              const followUpStatus = getFollowUpStatus({ follow_up_date: item.date });
              const isExtra = item.type === 'extra';

              const borderClass = item.isWatched
                ? 'border-2 border-purple-500 ring-4 ring-purple-500/20'
                : (isExtra
                  ? 'border-l-4 border-l-purple-500'
                  : `border-l-4 ${getFollowUpStatusColor(followUpStatus)}`);

              return (
                <div key={`${item.type}-${item.id}`} className={`rounded-lg overflow-hidden hover:shadow-lg transition-all flex flex-col bg-white dark:bg-gray-900 ${borderClass} relative`}>
                  {/* Watched Badge/Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleWatched(item);
                    }}
                    className={`absolute top-2 right-2 z-10 p-2 rounded-full transition-all shadow-md transform hover:scale-110 active:scale-95 ${item.isWatched
                      ? 'bg-purple-100 text-purple-600 hover:bg-purple-200 dark:bg-purple-900/50 dark:text-purple-300'
                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-500 hover:text-purple-500'
                      }`}
                    title={item.isWatched ? "İzlemeyi Bırak" : "İzlemeye Al"}
                  >
                    {item.isWatched ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>

                  {/* Üst Kısım - Follow-up Tarihi veya Tamamlanma Tarihi */}
                  {activeTab === 'active' && lead.follow_up_date && (
                    <div className={`${item.isWatched ? 'bg-gradient-to-r from-purple-100 to-fuchsia-100 dark:from-purple-900/40 dark:to-fuchsia-900/40 border-purple-400 dark:border-purple-500' : 'bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 border-yellow-400 dark:border-yellow-600'} p-3 border-b-2`}>
                      <div className="flex items-center space-x-2">
                        <Calendar className={`w-4 h-4 ${item.isWatched ? 'text-purple-700 dark:text-purple-300' : 'text-yellow-700 dark:text-yellow-400'}`} />
                        <div>
                          <p className={`text-xs font-medium ${item.isWatched ? 'text-purple-700 dark:text-purple-300' : 'text-yellow-700 dark:text-yellow-400'}`}>
                            {isExtra ? 'Ekstra Takip Tarihi' : 'Takip Tarihi'}
                          </p>
                          <p className={`text-sm font-bold ${item.isWatched ? 'text-purple-900 dark:text-purple-100' : 'text-yellow-900 dark:text-yellow-300'}`}>
                            {format(new Date(item.date), 'dd MMM yyyy', { locale: tr })}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'completed' && item.completedAt && (
                    <div className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 p-3 border-b-2 border-green-400 dark:border-green-600">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-700 dark:text-green-400" />
                        <div>
                          <p className="text-xs font-medium text-green-700 dark:text-green-400">Tamamlanma Tarihi</p>
                          <p className="text-sm font-bold text-green-900 dark:text-green-300">
                            {format(new Date(item.completedAt), 'dd MMM yyyy', { locale: tr })}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* For reminders without specific completed_at but are completed */}
                  {activeTab === 'completed' && isExtra && item.isCompleted && !item.completedAt && (
                    <div className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 p-3 border-b-2 border-green-400 dark:border-green-600">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-700 dark:text-green-400" />
                        <p className="text-sm font-bold text-green-900 dark:text-green-300">Tamamlandı</p>
                      </div>
                    </div>
                  )}

                  {/* Orta Kısım - Lead Bilgisi */}
                  <div className="p-4 flex-1">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">
                          {(lead.first_name?.[0] || '') + (lead.last_name?.[0] || '')}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {lead.first_name} {lead.last_name}
                          </h4>
                          {isExtra && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">EKSTRA</span>}
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className={`w-1.5 h-1.5 rounded-full ${getPriorityColor(lead.priority)}`} />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {lead.priority === 'high' ? 'Yüksek' :
                              lead.priority === 'medium' ? 'Orta' : 'Düşük'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* İletişim Bilgileri */}
                    <div className="space-y-2 mb-3">
                      {isExtra && (
                        <div className="p-2 bg-purple-50 dark:bg-purple-900/10 rounded border border-purple-100 dark:border-purple-800 mb-2">
                          <p className="text-xs font-bold text-purple-700 dark:text-purple-400 mb-1">Takip Başlığı:</p>
                          <p className="text-sm text-gray-800 dark:text-gray-200">{item.title}</p>
                        </div>
                      )}

                      {lead.emails && lead.emails.length > 0 && (
                        <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{lead.emails[0]}</span>
                        </div>
                      )}
                      {lead.phone && (
                        <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{lead.phone}</span>
                        </div>
                      )}
                    </div>

                    {/* Follow-up Contact */}
                    {(lead.follow_up_name || lead.follow_up_surname) && (
                      <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-xs font-medium text-blue-900 dark:text-blue-300 mb-1">Follow-up Kişisi</p>
                        <p className="text-xs text-blue-800 dark:text-blue-400">{lead.follow_up_name} {lead.follow_up_surname}</p>
                        {lead.follow_up_phone && (
                          <p className="text-xs text-blue-700 dark:text-blue-500 mt-1">{lead.follow_up_phone}</p>
                        )}
                      </div>
                    )}

                    {/* Follow-up Notu */}
                    {lead.follow_up_notes && (
                      <div className="mb-3 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <p className="text-xs font-medium text-purple-900 dark:text-purple-300 mb-1">Follow-up Notu</p>
                        <p className="text-xs text-purple-800 dark:text-purple-400 line-clamp-2">{lead.follow_up_notes}</p>
                      </div>
                    )}

                    {/* Oluşturan Kişi Badge */}
                    {item.created_by && (() => {
                      const creatorName = getCreatorName(item.created_by || '');
                      const colors = getCreatorColor(creatorName);
                      return (
                        <div className="flex items-center space-x-2">
                          <User className="w-3 h-3 text-gray-400" />
                          <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
                            {creatorName}
                          </span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Alt Kısım - Aksiyonlar */}
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                    {activeTab === 'active' ? (
                      <div className="flex flex-col space-y-2">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleLeadAction('view', item)}
                            className="flex-1 flex items-center justify-center space-x-1 bg-gray-600 hover:bg-gray-700 text-white px-2 py-1.5 rounded text-xs transition-colors"
                          >
                            <Eye className="w-3 h-3" />
                            <span>Görüntüle</span>
                          </button>
                          <button
                            onClick={() => handleLeadAction('call', item)}
                            className="flex-1 flex items-center justify-center space-x-1 bg-green-600 hover:bg-green-700 text-white px-2 py-1.5 rounded text-xs transition-colors"
                          >
                            <Phone className="w-3 h-3" />
                            <span>Ara</span>
                          </button>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleLeadAction('complete', item)}
                            className="flex-1 flex items-center justify-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded text-xs transition-colors"
                          >
                            <Check className="w-3 h-3" />
                            <span>Tamamla</span>
                          </button>
                          <button
                            onClick={() => handleLeadAction('delete', item)}
                            className="flex-1 flex items-center justify-center space-x-1 bg-red-600 hover:bg-red-700 text-white px-2 py-1.5 rounded text-xs transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                            <span>Sil</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleLeadAction('view', item)}
                          className="flex-1 flex items-center justify-center space-x-1 bg-gray-600 hover:bg-gray-700 text-white px-2 py-1.5 rounded text-xs transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          <span>Görüntüle</span>
                        </button>
                        <button
                          onClick={() => handleLeadAction('delete', item)}
                          className="flex-1 flex items-center justify-center space-x-1 bg-red-600 hover:bg-red-700 text-white px-2 py-1.5 rounded text-xs transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                          <span>Sil</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedLead(null);
          }}
          lead={selectedLead}
          onEdit={() => {
            setShowDetailModal(false);
            setShowEditModal(true);
          }}
          onUpdateLead={updateLead}
        />
      )}

      {/* Edit Lead Modal */}
      {selectedLead && (
        <EditLeadModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedLead(null);
          }}
          lead={selectedLead}
          onSave={async (updatedData) => {
            try {
              const { data, error } = await updateLead(selectedLead.id, updatedData);

              if (error) {
                console.error('Lead güncelleme hatası:', error);
                return { ok: false, error: 'Lead güncellenirken hata oluştu.' };
              }

              alert(`${selectedLead.first_name} ${selectedLead.last_name} başarıyla güncellendi!`);
              fetchLeads();
              return { ok: true, data };
            } catch (error) {
              console.error('Lead güncelleme hatası:', error);
              return { ok: false, error: 'Beklenmeyen bir hata oluştu.' };
            }
          }}
        />
      )}
    </div>
  );
};

export default FollowUps;
